/**
 * 同時視聴のリアルタイム通信で使う型（要件定義 17 章）。
 *
 * 834CH は全員が同じ番組を同じ位置で見ているので、部屋は 1 つでよい。
 * Worker とフロントエンドで共有する。
 */

/** 表示するコメント 1 件 */
export type LiveComment = {
  id: string
  /** 匿名の表示名。ユーザー登録はしない */
  name: string
  text: string
  /** 投稿時刻（epoch ミリ秒） */
  at: number
}

/** サーバー -> クライアント */
export type LiveServerMessage =
  /** 接続直後。いまの番組のコメントと視聴者数、割り当てられた表示名を送る */
  | {
      type: 'init'
      viewers: number
      comments: LiveComment[]
      /** いまの番組にこれまで集まったリアクション数 */
      reactions: ReactionCounts
      /** いまの動画に記録されているリアクション。これを手元で流し直す */
      replay: RecordedReaction[]
      name: string
      muted: boolean
    }
  /** 運営がコメントを止めた / 再開したとき */
  | { type: 'muted'; muted: boolean }
  | { type: 'viewers'; count: number }
  | { type: 'comment'; comment: LiveComment }
  /**
   * 誰かがリアクションしたとき。
   * count はその種別の、いまの番組での合計。
   * 「いま誰かが押した」演出と合計表示の両方をこれ 1 通でまかなう。
   */
  | { type: 'reaction'; kind: ReactionKind; count: number }
  /** 番組が切り替わってコメントとリアクションを一掃したとき */
  | { type: 'clear' }
  /**
   * 番組が変わったので、次の動画に記録されているリアクションを渡す。
   * いつ流すかはクライアントが決める（サーバーにタイマーを持たせない）。
   */
  | { type: 'replay'; reactions: RecordedReaction[] }
  /** 投稿を受け付けなかったとき */
  | { type: 'rejected'; reason: LiveRejectReason }

export type LiveRejectReason =
  | 'too_long'
  | 'too_fast'
  | 'empty'
  | 'blocked_word'
  | 'muted'
  | 'invalid'

/** クライアント -> サーバー */
export type LiveClientMessage =
  | { type: 'comment'; text: string }
  | { type: 'reaction'; kind: ReactionKind }
  /**
   * いま放送している番組の開始時刻と動画を知らせる。
   * サーバーは番組表を持たないので、これが番組の切り替わりを知る唯一の手がかりになる。
   *
   * videoId はリアクションの記録先を決めるためだけに使う。
   * 申告を信じているが、番組が進んだときにしか受け付けず、
   * 1 本あたりの記録件数にも上限があるので、荒らせる幅は狭い。
   */
  | { type: 'program'; programStartAt: string; videoId?: string }

/** 部屋が保持するコメントの上限 */
export const COMMENT_MAX_KEEP = 200

/** 未来の番組として受け付ける余裕。クライアントとの時計のずれを吸収する分だけ */
const PROGRAM_FUTURE_TOLERANCE_MS = 60_000

/**
 * 知らされた番組が、部屋がいま見ている番組より先へ進んでいるか。
 *
 * コメントは番組の切り替わりで消える。時間では消えない（要件定義 14 章）。
 * 切り替えの判断はクライアントの申告に頼るので、次の値は受け付けない。
 *
 * - 壊れた値・古い値: 巻き戻して前の番組のコメントを蘇らせないため
 * - 未来の値: これを通すと、以降の本物の切り替わりが「古い値」になって
 *   コメントが二度と消えなくなる（1 通で荒らせてしまう）
 */
export const isProgramAdvanced = (
  current: string | null,
  incoming: unknown,
  nowMs: number,
): incoming is string => {
  if (typeof incoming !== 'string' || incoming === '') return false
  const incomingMs = Date.parse(incoming)
  if (!Number.isFinite(incomingMs)) return false
  if (incomingMs > nowMs + PROGRAM_FUTURE_TOLERANCE_MS) return false
  if (current === null) return true
  return incomingMs > Date.parse(current)
}

/** コメントの文字数上限 */
export const COMMENT_MAX_LENGTH = 80

/** 同じ接続から続けて投稿できる間隔 */
export const COMMENT_MIN_INTERVAL_MS = 5000

/** コメントの受け付け設定（運営が KV で切り替える） */
export type Moderation = {
  /** true の間はコメントを一切受け付けない */
  muted: boolean
  /** 含まれていたら弾く語 */
  blockedWords: string[]
}

export const DEFAULT_MODERATION: Moderation = { muted: false, blockedWords: [] }

/** 全角・半角や大文字小文字の違いで NG ワードをすり抜けないようにそろえる */
export const normalizeForMatch = (text: string): string =>
  text.normalize('NFKC').toLowerCase().replace(/\s+/g, '')

export const containsBlockedWord = (text: string, blockedWords: string[]): boolean => {
  if (blockedWords.length === 0) return false
  const normalized = normalizeForMatch(text)
  return blockedWords.some((word) => {
    const target = normalizeForMatch(word)
    return target !== '' && normalized.includes(target)
  })
}

export type ValidateCommentInput = {
  text: string
  /** 同じ接続の直近の投稿時刻（epoch ミリ秒） */
  lastPostAt: number
  nowMs: number
  moderation: Moderation
}

/**
 * 投稿を受け付けてよいか判定する。
 *
 * 「いずれ消える」ことは荒れ対策の代わりにはならない。
 * 消えると後から検証もできないので、入り口で止める（要件定義 14 章）。
 */
export const validateComment = (input: ValidateCommentInput): LiveRejectReason | null => {
  if (input.moderation.muted) return 'muted'

  const text = input.text.trim()
  if (text === '') return 'empty'
  if ([...text].length > COMMENT_MAX_LENGTH) return 'too_long'
  if (input.nowMs - input.lastPostAt < COMMENT_MIN_INTERVAL_MS) return 'too_fast'
  if (containsBlockedWord(text, input.moderation.blockedWords)) return 'blocked_word'

  return null
}

export const rejectReasonText = (reason: LiveRejectReason): string => {
  switch (reason) {
    case 'too_long':
      return `${COMMENT_MAX_LENGTH} 文字までです`
    case 'too_fast':
      return '少し間をあけて投稿してください'
    case 'empty':
      return 'コメントを入力してください'
    case 'blocked_word':
      return 'この内容は投稿できません'
    case 'muted':
      return 'いまコメントを受け付けていません'
    default:
      return '投稿できませんでした'
  }
}

// ------------------------------------------------------------ リアクション

/**
 * リアクションの種類（要件定義 17 章）。
 *
 * コメントを書くほどではない人が、放送に加われるようにするための機能。
 * 種類を増やすほど「どれを押すか」を考えさせてしまうので、3 つに絞っている。
 */
export const REACTION_KINDS = ['clap', 'heart', 'fire'] as const

export type ReactionKind = (typeof REACTION_KINDS)[number]

/** 種別ごとのリアクション数 */
export type ReactionCounts = Record<ReactionKind, number>

export const REACTION_EMOJI: Record<ReactionKind, string> = {
  clap: '👏',
  heart: '💙',
  fire: '🔥',
}

export const REACTION_LABELS: Record<ReactionKind, string> = {
  clap: '拍手',
  heart: 'すき',
  fire: 'アガる',
}

export const emptyReactionCounts = (): ReactionCounts => ({ clap: 0, heart: 0, fire: 0 })

export const isReactionKind = (value: unknown): value is ReactionKind =>
  typeof value === 'string' && (REACTION_KINDS as readonly string[]).includes(value)

/** 1 件でもリアクションが付いているか */
export const hasReactions = (counts: ReactionCounts): boolean =>
  REACTION_KINDS.some((kind) => counts[kind] > 0)

/**
 * 同じ接続からリアクションを送れる間隔。
 *
 * コメントより短い。連打できる手触りが要るいっぽう、
 * 1 回の押下がそのまま全接続への配信になるので、青天井にはしない。
 */
export const REACTION_MIN_INTERVAL_MS = 400

export type CanReactInput = {
  kind: unknown
  /** 同じ接続の直近のリアクション時刻（epoch ミリ秒） */
  lastReactionAt: number
  nowMs: number
  moderation: Moderation
}

/**
 * リアクションを受け付けてよいか判定する。
 *
 * コメントと違って弾いた理由は返さない。
 * 押しても何も起きないだけにしておく方が、連打の手触りを壊さない。
 * 全体ミュート中はコメントと同じく止める（荒れているときに残す口を作らない）。
 */
export const canReact = (input: CanReactInput): input is CanReactInput & { kind: ReactionKind } => {
  if (!isReactionKind(input.kind)) return false
  if (input.moderation.muted) return false
  return input.nowMs - input.lastReactionAt >= REACTION_MIN_INTERVAL_MS
}

// ------------------------------------------------------------ リアクションの記録

/**
 * 記録したリアクション 1 件（要件定義 17 章）。
 *
 * 同じ動画が次に放送されたとき、同じところで流し直すために
 * 「動画の先頭から何秒の時点で押されたか」で持つ。
 * 誰が押したかは持たない（記録するのは反応があった位置だけ）。
 */
export type RecordedReaction = {
  /** 動画の先頭からの秒数 */
  at: number
  kind: ReactionKind
}

/**
 * 1 本の動画について覚えておくリアクションの上限。
 *
 * 放送のたびに増え続けると、いつか粒だらけになって画面が読めなくなる。
 * 溜まりきったらそれ以上は記録しない。
 */
export const RECORDED_REACTION_MAX = 150

/** 記録を安全に読み直す。壊れた値や上限超えは捨てる */
export const parseRecording = (value: unknown): RecordedReaction[] => {
  if (!Array.isArray(value)) return []
  return value
    .filter(
      (item): item is RecordedReaction =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as RecordedReaction).at === 'number' &&
        Number.isFinite((item as RecordedReaction).at) &&
        (item as RecordedReaction).at >= 0 &&
        isReactionKind((item as RecordedReaction).kind),
    )
    .slice(0, RECORDED_REACTION_MAX)
}

/**
 * リアクションが押された位置（動画の先頭からの秒数）。
 *
 * 番組は動画 1 本をまるごと流すので、番組の開始からの経過秒が
 * そのまま動画の先頭からの秒数になる。
 * 番組を知らないうちや、時計が巻き戻っているときは記録しない。
 */
export const recordedAtSeconds = (programStartAt: string | null, nowMs: number): number | null => {
  if (!programStartAt) return null
  const startMs = Date.parse(programStartAt)
  if (!Number.isFinite(startMs)) return null

  const at = Math.round((nowMs - startMs) / 1000)
  return at >= 0 ? at : null
}

/** 記録済みのものへ、今回ぶんを時刻順に混ぜる。上限で打ち切る */
export const mergeRecording = (
  stored: RecordedReaction[],
  added: RecordedReaction[],
): RecordedReaction[] =>
  [...stored, ...added].sort((a, b) => a.at - b.at).slice(0, RECORDED_REACTION_MAX)

/**
 * これから流す粒だけを、待ち時間つきで返す。
 *
 * @param elapsedMs 番組が始まってからの経過ミリ秒
 */
export const replaySchedule = (
  recorded: RecordedReaction[],
  elapsedMs: number,
): { kind: ReactionKind; delayMs: number }[] => {
  const schedule: { kind: ReactionKind; delayMs: number }[] = []
  for (const item of recorded) {
    const delayMs = item.at * 1000 - elapsedMs
    // すでに過ぎたものは流さない。途中から見た人に、頭の分をまとめて浴びせない
    if (delayMs >= 0) schedule.push({ kind: item.kind, delayMs })
  }
  return schedule
}
